/**
 * Casos de clientes (`/casos/:slug`).
 *
 * Só entra caso com autorização do cliente. Números vêm do banco de produção,
 * agregados e em percentual: nada de contato, conteúdo de mensagem ou volume
 * que o cliente não autorizou. O depoimento é o texto que o cliente mandou.
 */
export interface CaseMetric {
  value: string;
  label: string;
}

export interface CustomerCase {
  slug: string;
  company: string;
  title: string;
  description: string;
  summary: string;
  /** AAAA-MM-DD */
  published: string;
  updated: string;
  author: { name: string; role: string };
  /** Frase curta para a home. */
  highlight: string;
  quote: string[];
  /** Só números medidos no banco de produção. Vazio = caso publicado sem métricas. */
  metrics: CaseMetric[];
  /** Como os números foram medidos. Obrigatório quando há métricas. */
  method?: string;
  markdown: string;
}

export const cases: CustomerCase[] = [
  {
    slug: 'protvaco',
    company: 'Protvaço',
    title: 'Protvaço: cobrança pelo WhatsApp oficial com 94% de entrega',
    description:
      'Como o financeiro da Protvaço cobra pelo WhatsApp oficial com a Operax: a entrega subiu de 87% para 94% e a falha caiu de 10,5% para 6,3%.',
    summary:
      'A Protvaço usa a Operax desde julho de 2026 para cobrar clientes pelo WhatsApp oficial da Meta. No primeiro mês, 87% das cobranças foram entregues. No segundo, 94%. A taxa de falha caiu de 10,5% para 6,3%, e cerca de 6 em cada 10 cobranças são lidas.',
    published: '2026-09-17',
    updated: '2026-09-17',
    author: { name: 'Luiza', role: 'Setor financeiro da Protvaço' },
    highlight:
      'A Operax trouxe mais praticidade, organização e controle para o nosso processo de cobrança.',
    quote: [
      'Desde a contratação da Operax, percebemos uma melhora significativa no processo de cobrança da Protvaço. Antes, tínhamos bastante dificuldade para realizar disparos em massa pelo WhatsApp, principalmente devido aos bloqueios e limitações da Meta, o que acabava prejudicando o alcance das nossas cobranças e, consequentemente, o atingimento das metas.',
      'Com a utilização da Operax, esse processo ficou muito mais simples e organizado. Por ser uma plataforma integrada e autorizada pela Meta, conseguimos realizar os envios de forma mais estruturada, reduzindo bastante as dificuldades que enfrentávamos anteriormente.',
      'Outro ponto muito positivo é a centralização das informações dentro da própria plataforma. Além dos atendimentos, conseguimos acompanhar relatórios, custos dos disparos, resultados de ganhos e outras informações importantes para o acompanhamento do setor.',
      'A possibilidade de monitorar os resultados em tempo real também facilita bastante a gestão, pois conseguimos entender melhor o desempenho das ações de cobrança e acompanhar os resultados alcançados.',
      'De forma geral, considero que a Operax trouxe mais praticidade, organização e controle para o nosso processo de cobrança. A ferramenta facilitou a comunicação com os clientes e também nos deu uma visão muito mais clara dos resultados e dos custos envolvidos nas operações.',
    ],
    metrics: [
      { value: '94%', label: 'das cobranças entregues no segundo mês (eram 87%)' },
      { value: '6,3%', label: 'de falha no envio (eram 10,5%)' },
      { value: '6 em 10', label: 'cobranças lidas pelo cliente' },
    ],
    method:
      'Mensagens de cobrança enviadas por modelo aprovado pela Meta, de agosto a 17 de setembro de 2026. Entregue conta mensagem com confirmação de entrega ou leitura. Números agregados, sem dado de contato.',
    markdown: `## O problema

O setor financeiro da Protvaço cobra clientes pelo WhatsApp. Antes da Operax, os envios em massa esbarravam em bloqueios e limites da Meta. Parte das cobranças não chegava, e isso pesava direto na meta do setor.

## O que mudou

A cobrança passou a sair pela API oficial do WhatsApp, com modelos de mensagem aprovados pela Meta. A Operax é Provedor de Tecnologia verificado pela Meta, então o número da Protvaço envia pelo canal autorizado, sem depender de aparelho ou de aplicativo não oficial.

No mesmo lugar, a equipe acompanha:

- as conversas com cada cliente depois da cobrança;
- o custo de cada disparo, que a Meta cobra direto na conta da empresa;
- o resultado das ações de cobrança em tempo real.

## Resultado em dois meses

| Mês | Entregues | Falharam |
| --- | --- | --- |
| Agosto de 2026 | 87,1% | 10,5% |
| Setembro de 2026 (até dia 17) | 93,7% | 6,3% |

A entrega subiu quase 7 pontos de um mês para o outro. Com o número em uso contínuo e os modelos ajustados, a Meta passa a recusar menos envios.`,
  },
  {
    slug: 'consultorio-dr-lucas',
    company: 'Consultório do Dr. Lucas',
    title: 'Consultório do Dr. Lucas: o paciente agenda sozinho pelo WhatsApp',
    description:
      'Como o consultório do Dr. Lucas trocou o atendimento manual no WhatsApp por uma jornada automática em que o próprio paciente escolhe o horário e agenda.',
    summary:
      'O consultório do Dr. Lucas atendia cada paciente de forma individual e manual pelo WhatsApp, o que tomava tempo da equipe e fazia o paciente esperar pelo retorno. Com a Operax, o próprio paciente acessa o atendimento, vê as opções disponíveis, escolhe o horário e agenda. A equipe passou a dedicar o tempo ao que realmente precisa de atenção humana.',
    published: '2026-09-29',
    updated: '2026-09-29',
    author: { name: 'Dr. Lucas', role: 'consultório médico' },
    highlight: 'O Operax nos ajudou a transformar o WhatsApp em uma ferramenta muito mais estruturada de atendimento.',
    quote: [
      'A implantação da Operax trouxe uma mudança muito positiva para o atendimento do consultório.',
      'Antes, grande parte do processo era realizada de forma manual, com atendimentos individuais pelo WhatsApp, o que exigia muito tempo da equipe e, em alguns momentos, fazia com que a paciente precisasse aguardar mais para receber um retorno.',
      'Com a automatização, conseguimos ganhar escala, organização e agilidade. Hoje, o próprio paciente consegue acessar o atendimento, visualizar as opções disponíveis, escolher o melhor horário e realizar seu agendamento de maneira muito mais rápida e prática.',
      'O ganho foi para os dois lados: para nós, que reduzimos significativamente o trabalho operacional e conseguimos direcionar nosso tempo para atividades que realmente precisam da atenção da equipe; e para o paciente, que passou a ter uma experiência mais ágil, organizada e eficiente.',
      'O Operax nos ajudou a transformar o WhatsApp em uma ferramenta muito mais estruturada de atendimento, trazendo produtividade para o consultório sem perder a qualidade na experiência da paciente.',
    ],
    metrics: [],
    markdown: `## O problema

O agendamento do consultório acontecia pelo WhatsApp, uma conversa de cada vez. A equipe respondia manualmente cada paciente, e em horários de mais movimento o retorno demorava.

## O que mudou

O atendimento pelo WhatsApp passou a seguir uma jornada automática. O próprio paciente:

- inicia o atendimento pelo WhatsApp do consultório;
- vê as opções e os horários disponíveis;
- escolhe o melhor horário e conclui o agendamento, sem esperar alguém responder.

## Resultado

O consultório ganhou escala sem aumentar o trabalho manual da equipe, que agora dedica o tempo ao que precisa de atenção humana. Para o paciente, marcar uma consulta ficou mais rápido e organizado.`,
  },
  {
    slug: 'metodo-stop-gastos',
    company: 'Fernanda Educação Financeira',
    title: 'Método STOP Gastos: jornada do novo aluno no automático pelo WhatsApp',
    description:
      'Como a Fernanda Educação Financeira automatizou pelo WhatsApp as primeiras etapas de quem chega ao Método STOP Gastos, da orientação inicial à liberação do app.',
    summary:
      'Cada pessoa interessada no Método STOP Gastos era atendida de forma individual e manual. Com a Operax, quem chega pelo WhatsApp recebe as orientações iniciais, responde uma pesquisa prévia e avança nas primeiras etapas no automático, inclusive com a liberação do acesso ao aplicativo. Mais pessoas passaram a ser atendidas ao mesmo tempo, sem aumentar o trabalho da equipe na mesma proporção.',
    published: '2026-09-29',
    updated: '2026-09-29',
    author: { name: 'Fernanda', role: 'Fernanda Educação Financeira, Método STOP Gastos' },
    highlight:
      'A Operax nos permitiu automatizar processos que antes consumiam muitas horas de trabalho.',
    quote: [
      'A Operax trouxe um ganho enorme de produtividade e escala para o nosso atendimento.',
      'Antes, grande parte do contato com cada pessoa interessada no Método STOP Gastos precisava ser conduzida individualmente e de forma manual. Com a automatização, conseguimos estruturar toda essa jornada desde o primeiro contato.',
      'Hoje, quando uma pessoa chega querendo conhecer o Método STOP Gastos, ela já consegue receber as orientações iniciais, responder uma pesquisa prévia e avançar nas primeiras etapas do processo de forma automatizada, inclusive com a liberação do acesso ao aplicativo.',
      'Isso tornou o atendimento muito mais rápido e organizado, além de permitir que mais pessoas sejam atendidas simultaneamente, sem que precisemos aumentar proporcionalmente o trabalho operacional.',
      'Para nós, o principal resultado foi conseguir ganhar tempo, produtividade e escala, enquanto, para o cliente, a experiência ficou mais simples, ágil e fluida.',
      'A Operax nos permitiu automatizar processos que antes consumiam muitas horas de trabalho, mantendo uma jornada de atendimento organizada e eficiente do início ao fim.',
    ],
    metrics: [],
    markdown: `## O problema

Cada pessoa que chegava querendo conhecer o Método STOP Gastos era conduzida individualmente, à mão. O atendimento crescia na mesma proporção que o número de interessados.

## O que mudou

A jornada foi estruturada desde o primeiro contato no WhatsApp. Quem chega:

- recebe as orientações iniciais;
- responde uma pesquisa prévia;
- avança nas primeiras etapas do processo, até a liberação do acesso ao aplicativo.

Tudo isso acontece no automático, sem depender de alguém da equipe para cada passo.

## Resultado

Mais pessoas passaram a ser atendidas ao mesmo tempo, sem aumentar o trabalho operacional na mesma proporção. Para quem chega, a experiência ficou mais simples e rápida do início ao fim.`,
  },
  {
    slug: 'supermercado-somar',
    company: 'Supermercado Somar',
    title: 'Supermercado Somar: atendimento ao cliente pelo WhatsApp oficial',
    description:
      'O Supermercado Somar atende os clientes pelo WhatsApp com a Operax. O Davi, líder de tecnologia, conta como estão sendo a implantação e os ajustes para a loja.',
    summary:
      'O Supermercado Somar contratou a Operax para atender os clientes pelo WhatsApp oficial. A implantação está em andamento, e a Operax vem sendo ajustada à rotina do supermercado. O Davi, líder de tecnologia, conta o que esperam do novo canal e como tem sido o acompanhamento.',
    published: '2026-11-04',
    updated: '2026-11-04',
    author: { name: 'Davi', role: 'líder de tecnologia do Supermercado Somar' },
    highlight:
      'Esse cuidado em personalizar o sistema para o nosso supermercado mostra o seu comprometimento e faz toda a diferença.',
    quote: [
      'Gostaríamos de deixar aqui um feedback muito positivo sobre o sistema da Operax que contratamos para fazer o atendimento ao cliente via WhatsApp aqui do supermercado.',
      'Desde o início, ficou claro que a ferramenta vai fazer uma grande diferença no nosso dia a dia: o atendimento ficará mais organizado, mais ágil e muito mais prático tanto para a nossa equipe quanto para os nossos clientes, que terão respostas mais rápidas e um canal de contato eficiente.',
      'Queremos destacar especialmente a disponibilidade do Renan. Que sempre esteve presente, atento às nossas necessidades e pronto para nos ajudar sempre que precisamos. Também agradecemos por todas as modificações e ajustes que você está fazendo no Operax para que ele se adapte à nossa realidade e nos atenda da melhor forma possível. Esse cuidado em personalizar o sistema para o nosso supermercado mostra o seu comprometimento e faz toda a diferença.',
      'Estamos muito satisfeitos com a parceria e confiantes de que esse sistema vai elevar ainda mais a qualidade do nosso atendimento.',
    ],
    metrics: [],
    markdown: `## O ponto de partida

O Supermercado Somar queria atender os clientes pelo WhatsApp de um jeito mais organizado, com a equipe respondendo mais rápido e o cliente tendo um canal de contato que funciona.

## O que está sendo feito

A Operax liga o WhatsApp do supermercado pela API oficial da Meta e organiza as conversas num só lugar para a equipe. Durante a implantação, o sistema vem sendo ajustado ao jeito que a loja trabalha, em vez de a loja se adaptar ao sistema.

## Onde está hoje

A implantação está em andamento. Ainda não há números de atendimento para publicar: eles entram aqui quando houver período medido e o supermercado autorizar.`,
  },
];

export function findCase(slug: string): CustomerCase | undefined {
  return cases.find((c) => c.slug === slug);
}
